'use strict';
const _ = require('lodash');
const workerUtils = require('./workerUtils');
const events = require('../events');
const errors = require('../errors');

function getControllersData(configs) {
  return Object.keys(configs).map(function(controllerId) {
    const type = configs[controllerId].controllerData.controllerType;
    const config = configs[controllerId].controllerData.settings;
    const connections = configs[controllerId].connections || [];
    return {
      type,
      config,
      connections,
      $w: self.wix.getSelector(controllerId)
    };
  });
}

function getSemiNativeCompsData(configs) {
  return Object.keys(configs).map(dataItemId => {
    const widgetId = configs[dataItemId].compData.widgetId;
    const compId = configs[dataItemId].compId;
    const styleParams = configs[dataItemId].styleParams;
    const publicData = configs[dataItemId].publicData;
    const semiNativeSDK = self.semiNative.getScopedSDK(compId);
    let instance = configs[dataItemId].instance;
    if (instance) {
      instance = JSON.parse(self.atob(instance.split('.')[1]));
      instance.raw = configs[dataItemId].instance;
    }
    return {
      widgetId,
      compId,
      instance,
      styleParams,
      publicData,
      $w: self.wix.getSelector(dataItemId),
      sdk: semiNativeSDK
    };
  });
}

function registerControllerEvents(wix, controllerId, configs) { //TODO: Add context
  configs[controllerId].controllerBehaviors.forEach(function(item) {
    var fn = wix.__INTERNAL__.getEventHandler(item.behavior.params.callbackId);
    if (fn) {
      wix.emitter.on(controllerId, item.action.name, fn);
    }
  });
}

function getControllersReadyResolver(app) {
  var controllersReady;
  app.controllersReady = new Promise(resolve => {
    controllersReady = resolve;
  });
  return controllersReady;
}

function setRemoteDomChannel(workerId) {
  return self.remoteDOM.setChannel({
    postMessage: function(msg) {
      msg = {
        intent: 'WIX_CODE',
        type: 'OTW',
        data: msg,
        contextId: workerId
      };
      self.postMessage(msg);
    },

    addEventListener: function(evtName, handler) {
      self.addEventListener(evtName, function(ev) {
        if (ev.data.intent === 'WIX_CODE_RESPONSE' && ev.data.type === 'OTW') {
          handler(ev.data);
        }
      });
    }
  });
}

function callAppCreateSemiNativeComponents(workerId, app, configs = {}, reportError) {
  const ids = Object.keys(configs);
  if (ids.length === 0) {
    return Promise.resolve([]);
  }

  return setRemoteDomChannel(workerId).then(() => {
    let semiNativeComps = [];
    try {
      semiNativeComps = app.module.createSemiNativeComponents(getSemiNativeCompsData(configs));
    } catch (err) {
      reportError()(err);
    }
    const wrappedSemiNativeComps = semiNativeComps.map((comp, index) => {
      const p = Promise.resolve(comp);
      p.catch(reportError(ids[index]));
      return p;
    });
    return Promise.all(wrappedSemiNativeComps);
  });
}

function callAppCreateControllers(app, configs, reportError) {
  const controllerIds = Object.keys(configs);
  if (!controllerIds.length) {
    return Promise.resolve([]);
  }
  var controllers = [];
  try {
    controllers = app.module.createControllers(getControllersData(configs));
  } catch (err) {
    reportError()(err);
  }
  var wrappedControllers = controllers.map(function(controller, index) {
    var p = Promise.resolve(controller);
    p.catch(reportError(controllerIds[index]));
    return p;
  });
  return Promise.all(wrappedControllers);
}

function renderSemiNativeCompsOnReady(semiNativeComps, semiNativeCompConfigs, wix, app, workerId) {
  const semiNativeIds = Object.keys(semiNativeCompConfigs);
  app.semiNativeComponents = semiNativeIds.reduce((acc, dataItemId, index) => {
    const compId = semiNativeCompConfigs[dataItemId].compId;
    acc[compId] = semiNativeComps[index];
    return acc;
  }, {});
  semiNativeComps.forEach(function(semiNativeComp, index) {
    const dataItemId = semiNativeIds[index];
    const $w = wix.getSelector(dataItemId);
    $w.onReady(function() {
      return Promise
        .resolve(semiNativeComp.render(self.ReactDOM, self.remoteDOM))
        .catch(function(err) {
          workerUtils.reportBI(errors.PAGE_READY, {
            app_id: app.id,
            worker_id: workerId,
            controller_id: dataItemId,
            controller_type: 'semiNative',
            error: err && err.message
          });
        });
    });
  });
}

function getControllersOnReady(controllers, wix, controllerConfigs, app, workerId) {
  const controllerIds = Object.keys(controllerConfigs);
  app.controllers = controllerIds.reduce(function(acc, controllerId, index) {
    var compId = controllerConfigs[controllerId].compId;
    acc[compId] = controllers[index];
    return acc;
  }, {});
  controllerIds.forEach(controllerId => registerControllerEvents(wix, controllerId, controllerConfigs));
  return controllers.map((controller, index) => {
    var controllerId = controllerIds[index];
    var $w = wix.getSelector(controllerId);
    var scopedGlobalSdkApis = wix.getScopedGlobalApis(controllerId);
    return {
      controllerId,
      dependencies: controllerConfigs[controllerId].dependencies || [],
      onReady: $w.onReady.bind($w, function() {
        if (controller.pageReady) {
          var pageReadyPromise = Promise.resolve(controller.pageReady($w, scopedGlobalSdkApis));
          pageReadyPromise.catch(function(err) {
            workerUtils.reportBI(errors.PAGE_READY, {
              app_id: app.id,
              worker_id: workerId,
              controller_id: controllerId,
              controller_type: controllerConfigs[controllerId].controllerData.controllerType,
              error: err && err.message
            });
          });
          return pageReadyPromise;
        }
        console.warn('controller.start is deprecated please export controller.pageReady method instead'); // eslint-disable-line no-console
        return controller.start($w);
      })
    };
  });
}

function callControllersReadyInOrder(controllersOnReady) {
  function visit(node) {
    if (node.entered || node.visited) {
      return;
    }
    node.entered = true;
    node.dependencies.forEach(controllerId => {
      const dep = _.find(controllersOnReady, {controllerId});
      if (dep) {
        visit(dep);
      }
    });
    node.visited = true;
    node.onReady();
  }
  let nextNode = _.find(controllersOnReady, node => !node.visited);
  while (nextNode) {
    visit(nextNode);
    nextNode = _.find(controllersOnReady, node => !node.visited);
  }
}


function handleInit({apps}, {workerId, getApp}) {
  if (!apps) {
    throw new Error('Init message data must include apps property');
  }
  const controllersOnReady = [];
  return Promise.all(Object.keys(apps)
    .map(appId => {
      const app = getApp(appId);
      if (!app) {
        console.error(`App with the id - ${appId} failed to load`);// eslint-disable-line no-console
        return Promise.resolve();
      }
      const {
        controllers: controllerConfigs = {},
        semiNativeComponents: semiNativeCompConfigs = {}
      } = apps[appId];
      var controllersReady = getControllersReadyResolver(app);
      let beforeInit;
      return app.ready
        .then(() => {
          beforeInit = Date.now();
          const reportError = controllerId => err => {
            const params = {
              app_id: appId,
              worker_id: workerId,
              error: err && err.message,
              time_spent: Date.now() - beforeInit
            };
            if (controllerId) {
              params.controller_id = controllerId;
            }
            workerUtils.reportBI(errors.CREATE_CONTROLLERS, params);
          };
          return Promise.all([callAppCreateControllers(app, controllerConfigs, reportError), callAppCreateSemiNativeComponents(workerId, app, semiNativeCompConfigs, reportError)]);
        })
        .then(function([controllers, semiNativeComps]) {
          var afterInit = Date.now();
          workerUtils.reportBI(events.CREATE_CONTROLLERS, {
            app_id: appId,
            worker_id: workerId,
            time_spent: afterInit - beforeInit
          });
          controllersReady();
          renderSemiNativeCompsOnReady(semiNativeComps, semiNativeCompConfigs, self.wix, app, workerId);
          controllersOnReady.push(...getControllersOnReady(controllers, self.wix, controllerConfigs, app, workerId));
        })
        .catch(controllersReady);
    }))
    .then(() => callControllersReadyInOrder(controllersOnReady));
}

module.exports = {
  handleInit: handleInit
};



// WEBPACK FOOTER //
// ./src/main/worker/initHandler.js